import IconButton from "@material-ui/core/IconButton";
import { makeStyles, Theme } from "@material-ui/core/styles";
import ReplyIcon from "@material-ui/icons/Reply";
import { RefObject, useLayoutEffect, useRef } from "react";
import { changeAppState, getAppState } from "../../../../../../common/appState";
import {
    replyTargetImageKey,
    replyToMessageKey,
} from "../../../../Markdown/CodeRender";
import { openOtherUserPanel } from "../../../../User/OtherUserPanel/functions";
import { OtherUser } from "../../../../User/types";
import { UserAvatar } from "../../../../User/UserAvatar/UserAvatar";
import { getMessagesPerDays } from "../../getMessagesPerDay";
import { Message, MessagesPerDays } from "../../types";
import { SentTime } from "./components/SentTime";
import { TheirPresent } from "./components/TheirPresent";
import { readMessage } from "./functions";
import { ImageMessage } from "./ImageMessage";
import { MessageMarkdown } from "./MessageMarkdown";

export function TheirMessage({
    them,
    message: {
        text,
        isRead,
        messageNo,
        minutesAgo,
        present,
        presentQuantity,
        isPresentOpen,
        isImage,
    },
    myUserId,
    setMessages,
    messagesCountToShow,
    openPresent,
    submitting,
    setSubmitting,
    targetUserIconColorNumber,
    setReplyTarget,
    inputRef,
}: {
    them: OtherUser;
    message: Message;
    myUserId: number;
    setMessages: (messages: MessagesPerDays) => void;
    messagesCountToShow: number;
    openPresent: () => Promise<void>;
    submitting: boolean;
    setSubmitting: (submitting: boolean) => void;
    targetUserIconColorNumber?: number;
    setReplyTarget: (text: string) => void;
    inputRef: RefObject<HTMLInputElement>;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const c = useStyles({ isImage });

    useLayoutEffect(() => {
        if (!isRead) {
            containerRef.current?.scrollIntoView();
            readMessage(
                them.userId,
                myUserId,
                messageNo,
                messagesCountToShow
            ).then(result => {
                if ("error" in result) {
                    return;
                }
                setMessages(getMessagesPerDays(result.messages));
                changeAppState(
                    "unreadMessages",
                    getAppState().unreadMessages.filter(
                        m =>
                            !(
                                m.fromUserId === them.userId &&
                                m.messageNo === messageNo
                            )
                    )
                );
            });
        }
    }, [isRead, myUserId, messageNo, setMessages, messagesCountToShow]);

    return (
        <div className={c.container} ref={containerRef}>
            <UserAvatar
                user={them}
                colorNumber={targetUserIconColorNumber ?? them.level}
                size={40}
                onClick={() => {
                    openOtherUserPanel(them.userId);
                }}
                className="pointer"
            />
            <div className={c.comment}>
                <div className={c.commentTail} />

                {isImage ? (
                    <ImageMessage
                        myUserId={myUserId}
                        targetUserId={them.userId}
                        messageNo={messageNo}
                        messageType={"TheirMessage"}
                    />
                ) : (
                    <div className={c.markdownContainer}>
                        <MessageMarkdown text={text} noLinkShadow />
                    </div>
                )}

                {present && (
                    <TheirPresent
                        present={present}
                        presentQuantity={presentQuantity}
                        isOpen={isPresentOpen}
                        openPresent={openPresent}
                        globalSubmitting={submitting}
                        setGlobalSubmitting={setSubmitting}
                    />
                )}
            </div>

            <div className={c.rightSpace}>
                <div className={c.replyButtonContainer}>
                    <IconButton
                        className={c.replyButton}
                        onClick={() => {
                            inputRef.current?.focus();

                            if (isImage) {
                                setReplyTarget(
                                    `${replyTargetImageKey}${messageNo}:${myUserId}:${them.userId}`
                                );
                                return;
                            }
                            const textWithoutPreviousReply = text
                                .split("```")
                                .filter(
                                    line => !line.startsWith(replyToMessageKey)
                                )
                                .join("")
                                .trim();
                            setReplyTarget(textWithoutPreviousReply);
                        }}
                    >
                        <ReplyIcon />
                    </IconButton>
                </div>
                <SentTime minutesAgo={minutesAgo} />
            </div>
        </div>
    );
}
const useStyles = makeStyles<Theme, { isImage: boolean }>(theme => ({
    container: {
        display: "flex",
        width: "100%",
        marginBottom: 10,
    },
    comment: ({ isImage }) => ({
        minHeight: 40,
        backgroundColor: theme.palette.grey[200],
        marginLeft: 20,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: isImage ? 3 : "13px 15px 15px",
        borderRadius: 10,
        position: "relative",
    }),
    commentTail: {
        position: "absolute",
        width: 20,
        height: 20,
        top: 10,
        left: -5,
        transform: "rotate(45deg)",
        backgroundColor: theme.palette.grey[200],
    },
    rightSpace: {
        width: 60,
        display: "flex",
        flexDirection: "column",
        padding: 5,
        fontSize: "small",
    },
    markdownContainer: {
        zIndex: 1,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
    },
    replyButtonContainer: {
        width: 29,
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    replyButton: { width: 30, height: 30, cursor: "pointer" },
}));
